import type { Announcement, Mention, MentionItem, RemoteConfig, Snapshot, Ticket, TicketMessage, TicketPriority, TicketType } from '../types';
import type { State } from './state';
import { SDK_VERSION } from './state';

/** Per-type `{total, unread}` for the list chips, plus an `all` row; computed before the type filter. */
export type TicketCounts = Partial<Record<TicketType | 'all', { total: number; unread: number }>>;

export interface TicketPage {
  tickets: Ticket[];
  unread: number;
  /** Tickets with an unread "@" mention of the verified user ("Bahsedilenler"). */
  mentions_unread?: number;
  counts?: TicketCounts;
  type?: string;
  page?: number;
  per_page?: number;
  total?: number;
  has_more?: boolean;
  /** §245: the verified user is a yetkili kişi of the customer — the "Pano" tab shows. */
  pano?: boolean;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`FETrace API ${status}`);
  }
}

/** §234: what the widget is told of a likely repeat. */
export interface SimilarTicket {
  id: number;
  reference: string;
  title: string;
  status: Ticket['status'];
}

/** Thin REST client for api.fetech.llc/v1 (PLAN.md §4). Uses the page's *original* fetch to avoid our own patch. */
export class ApiClient {
  constructor(
    private readonly state: State,
    private readonly rawFetch: typeof fetch = window.fetch.bind(window),
  ) {}

  /** `raw`: the body as an ArrayBuffer instead of JSON (a recording). */
  private async request<T>(method: string, path: string, body?: unknown, query?: Record<string, string>, raw = false): Promise<T> {
    const url = new URL(this.state.options.apiUrl + path);
    if (query) for (const [k, v] of Object.entries(query)) if (v !== '') url.searchParams.set(k, v);

    const res = await this.rawFetch(url.toString(), {
      method,
      // X-FE-Key duplicates the Bearer token for hosts that strip Authorization headers.
      headers: { Authorization: `Bearer ${this.state.options.key}`, 'X-FE-Key': this.state.options.key, 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      keepalive: method === 'POST',
    });
    if (raw && res.ok) return (await res.arrayBuffer()) as T;
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, data);
    return data as T;
  }

  private base() {
    return { sdk_version: SDK_VERSION, session: this.state.sessionPayload(), user: this.state.userPayload() };
  }

  private userQuery(): Record<string, string> {
    const u = this.state.identity;
    if (!u?.hash) return {};
    // `exp` is part of what the hash signs, so it has to travel with it on GETs too.
    return u.exp ? { user_id: String(u.id), hash: u.hash, exp: String(u.exp) } : { user_id: String(u.id), hash: u.hash };
  }

  config(): Promise<RemoteConfig> {
    return this.request<RemoteConfig>('GET', '/config');
  }

  status(): Promise<{ announcements: Announcement[] }> {
    return this.request('GET', '/status');
  }

  events(events: unknown[]): Promise<{ accepted: number; dropped: number }> {
    return this.request('POST', '/events', { ...this.base(), context: this.state.context, events });
  }

  createTicket(input: {
    title: string;
    description: string;
    type: TicketType;
    priority: TicketPriority;
    contact?: { name: string; email: string };
    snapshot: Snapshot;
    screenshot_token?: string;
    attachment_tokens?: string[];
  }): Promise<{ ticket: Ticket }> {
    return this.request('POST', '/tickets', { ...this.base(), page_url: location.href, ...input });
  }

  /**
   * §234 tekrar talep: the person's own open ticket on the same topic, asked before a new one goes out; `ticket` is null
   * when there is none. Only tickets this browser holds tokens of (or the verified user's) are looked at.
   */
  similar(title: string, description: string): Promise<{ ticket: SimilarTicket | null }> {
    return this.request('POST', '/tickets/similar', { ...this.base(), title, description, tokens: Object.values(this.state.ticketTokens()) });
  }

  /** `unread` is the whole-scope total whatever the page, chip or search term (badge source); `per_page: 1` makes a cheap poll. */
  listTickets(params: { q?: string; type?: string; page?: number; per_page?: number } = {}): Promise<TicketPage> {
    const tokens = Object.values(this.state.ticketTokens()).join(',');
    return this.request('GET', '/tickets', undefined, {
      ...this.userQuery(),
      tokens,
      q: params.q ?? '',
      type: params.type ?? '',
      page: params.page ? String(params.page) : '',
      per_page: params.per_page ? String(params.per_page) : '',
    });
  }

  ticket(id: number): Promise<{ ticket: Ticket }> {
    return this.request('GET', `/tickets/${id}`, undefined, { ...this.userQuery(), token: this.state.ticketTokens()[String(id)] ?? '' });
  }

  /** "İzle": one screen recording of a thread the viewer may read, as stored (gzip). */
  recording(id: number, attachment: number): Promise<ArrayBuffer> {
    return this.request('GET', `/tickets/${id}/recordings/${attachment}`, undefined, { ...this.userQuery(), token: this.state.ticketTokens()[String(id)] ?? '' }, true);
  }

  /** "Üste sabitle": the user's own ordering of their list; invisible to staff. */
  pinTicket(id: number, pinned: boolean): Promise<{ ticket: Ticket }> {
    return this.request('POST', `/tickets/${id}/pin`, { ...this.base(), token: this.state.ticketTokens()[String(id)] ?? '', pinned });
  }

  /** "Problem çözüldü": the user closes their own ticket while it is in review or waiting for them. */
  resolveTicket(id: number): Promise<{ ticket: Ticket }> {
    return this.request('POST', `/tickets/${id}/resolve`, { ...this.base(), token: this.state.ticketTokens()[String(id)] ?? '' });
  }

  /** "Nasıl yapılır" guide progress (DECISIONS.md §213): started / step / done ("Oldu") / failed ("Olmadı") / declined. */
  /** §245 "Pano": the cards (`excel`: the same as an .xlsx file); `q` = from, to, project_id. */
  pano<T>(q: Record<string, string>, excel = false): Promise<T> {
    return this.request<T>('GET', excel ? '/pano/excel' : '/pano', undefined, { ...this.userQuery(), ...q }, excel);
  }

  guideEvent(id: number, event: 'started' | 'step' | 'done' | 'failed' | 'declined', step?: number): Promise<{ ticket: Ticket }> {
    return this.request('POST', `/tickets/${id}/guide`, { ...this.base(), token: this.state.ticketTokens()[String(id)] ?? '', event, ...(step !== undefined ? { step } : {}) });
  }

  reply(id: number, body: string, attachmentTokens: string[] = [], replyTo?: number, mentions: number[] = []): Promise<{ message: TicketMessage }> {
    return this.request('POST', `/tickets/${id}/messages`, {
      ...this.base(),
      token: this.state.ticketTokens()[String(id)] ?? '',
      body,
      attachment_tokens: attachmentTokens,
      ...(replyTo ? { reply_to: replyTo } : {}),
      ...(mentions.length ? { mentions } : {}),
    });
  }

  /** "Cevapları gönder": answers to the questions of one block; already answered ones come back in `skipped`. */
  answer(id: number, messageId: number, answers: { question_id: number; body: string }[]): Promise<{ saved: number[]; skipped: number[] }> {
    return this.request('POST', `/tickets/${id}/answers`, { ...this.base(), token: this.state.ticketTokens()[String(id)] ?? '', message_id: messageId, answers });
  }

  /** "Bahsedilenler": tickets the verified user was "@"-mentioned in, newest first. */
  mentions(page = 1, perPage = 10): Promise<{ items: MentionItem[]; unread: number; total: number; has_more: boolean; page: number }> {
    return this.request('GET', '/mentions', undefined, { ...this.userQuery(), page: String(page), per_page: String(perPage) });
  }

  /** "@" picker: verified users of the project (names only). */
  mentionUsers(q: string, ticket: number): Promise<{ users: Mention[] }> {
    return this.request('GET', '/mentions/users', undefined, { ...this.userQuery(), q, ticket: String(ticket) });
  }

  /** `kind: 'replay'` = a screen recording; the server checks it and always takes it on its own upload url. */
  async upload(file: Blob, filename: string, kind?: 'replay'): Promise<string> {
    const mime = file.type || 'application/octet-stream';
    const presign = await this.request<{ token: string; upload_url: string; method: string; headers: Record<string, string> }>('POST', '/uploads/presign', {
      filename,
      mime,
      size: file.size,
      kind,
    });
    const own = this.state.isOwnApi(presign.upload_url);
    const res = await this.rawFetch(presign.upload_url, {
      method: presign.method || 'PUT',
      headers: { ...presign.headers, ...(own ? { Authorization: `Bearer ${this.state.options.key}`, 'X-FE-Key': this.state.options.key } : {}) },
      body: file,
    });
    if (!res.ok) throw new ApiError(res.status, null);
    return presign.token;
  }

  /** Fire-and-forget on page unload. sendBeacon cannot set headers → key in query, text/plain body. */
  beacon(events: unknown[]): boolean {
    if (typeof navigator.sendBeacon !== 'function') return false;
    const url = `${this.state.options.apiUrl}/events?key=${encodeURIComponent(this.state.options.key)}`;
    const payload = JSON.stringify({ ...this.base(), context: this.state.context, events });
    try {
      return navigator.sendBeacon(url, new Blob([payload], { type: 'text/plain' }));
    } catch {
      return false;
    }
  }
}
